/**
 * Minimal runtime polyfills for older WebKit (iOS 14 / Safari 14).
 * Syntax is handled by build.target; these cover APIs used by dependencies (mainly pdf.js).
 */

/**
 * Relative indexing, as in Array.prototype.at (Safari 15.4)
 * @param this Indexable value
 * @param index Index, negative counts from the end
 * @returns Element at index, or undefined
 */
function at(this: any, index: number) {
  const length = this.length;
  let i = Math.trunc(index) || 0;
  if (i < 0) { i += length; }
  return i < 0 || i >= length ? undefined : this[i];
}

const TypedArray = Object.getPrototypeOf(Int8Array);
[Array.prototype, String.prototype, TypedArray.prototype].forEach(proto => {
  if (!proto.at) {
    Object.defineProperty(proto, "at", { value: at, writable: true, configurable: true });
  }
});

/**
 * Deep clone of structured-cloneable data (Safari 15.4). Transfer lists are ignored.
 * @param value Value to clone
 * @param seen Already cloned objects, to preserve shared and circular references
 * @returns Cloned value
 */
const clone = (value: any, seen = new Map<any, any>()): any => {
  if (value === null || typeof value !== "object") { return value; }
  if (seen.has(value)) { return seen.get(value); }
  if (value instanceof Date) { return new Date(value.getTime()); }
  if (value instanceof RegExp) { return new RegExp(value.source, value.flags); }
  if (ArrayBuffer.isView(value)) { return (value as any).slice(); }
  if (value instanceof ArrayBuffer) { return value.slice(0); }
  if (typeof ImageBitmap !== "undefined" && value instanceof ImageBitmap) { return value; }

  if (value instanceof Map) {
    const result = new Map();
    seen.set(value, result);
    value.forEach((v, k) => result.set(clone(k, seen), clone(v, seen)));
    return result;
  }
  if (value instanceof Set) {
    const result = new Set();
    seen.set(value, result);
    value.forEach(v => result.add(clone(v, seen)));
    return result;
  }

  const result: any = Array.isArray(value) ? [] : {};
  seen.set(value, result);
  Object.keys(value).forEach(key => { result[key] = clone(value[key], seen); });
  return result;
};

if (typeof (globalThis as any).structuredClone !== "function") {
  (globalThis as any).structuredClone = (value: any) => clone(value);
}
